import classNames from 'classnames';
import type React from 'react';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import {
  getAccessibleTabIndex,
  getAriaButtonRoleAttribute,
  handleAccessibleButtonKeyDown,
} from '../Button/ariaHelpers';
import { Icon } from '../Icon/Icon';
import { useConditionalClickHandler } from '../hooks/useConditionalClickHandler';
import { getMenuItemClassNames } from './MenuItem.styles';
import type { MenuItemProps } from './MenuItem.types';
import { SubmenuItem } from './SubMenuItem';
import { renderIcon } from './helpers/helper';

export const MenuItem = ({
  items,
  label,
  size = 'default',
  disabled,
  selected,
  description,
  trailingIcon,
  leadingIcon,
  showArrow: showArrowArgument,
  className,
  iconClassName,
  onClick,
  renderBadge,
}: MenuItemProps) => {
  const expandable = (items && items.length > 0) ?? false;
  const [isOpen, setIsOpen] = useState(false);
  const showArrow = showArrowArgument ?? (expandable || isOpen);

  const renderArrow = () => {
    if (!showArrow) {
      return null;
    }

    if (!expandable) {
      return <Icon glyph='fa-angle-right' className='shrink-0' />;
    }

    if (isOpen) {
      return <Icon glyph='fa-angle-up' className='shrink-0' />;
    }

    return <Icon glyph='fa-angle-down' className='shrink-0' />;
  };

  const renderTrailingIcon = () =>
    renderIcon(trailingIcon) || renderIcon(iconClassName);
  const renderLeadingIcon = () => renderIcon(leadingIcon);

  const handleClick = useConditionalClickHandler<HTMLDivElement>(
    disabled,
    (e: React.MouseEvent) => {
      // Toggle the submenu if it is expandable and not disabled
      if (expandable) {
        setIsOpen(!isOpen);
      }

      onClick?.(e);
    },
  );

  return (
    <div
      className={twMerge(
        classNames(
          className,
          getMenuItemClassNames({
            size,
            isDisabled: disabled,
            selected,
            expandable,
            isOpen,
          }),
          // collapse menu item if no label or description
          label || description ? 'w-full' : 'w-min',
          !disabled && 'cursor-pointer',
        ),
      )}
      onClick={handleClick}
      {...getAriaButtonRoleAttribute()}
      tabIndex={getAccessibleTabIndex(disabled)}
      onKeyDown={(e) => handleAccessibleButtonKeyDown(e, disabled)}
    >
      <div className='flex gap-xs items-center'>
        {renderLeadingIcon()}
        {(label || description) && (
          <div className='flex flex-col flex-1'>
            <div className='flex flex-row gap justify-between items-center'>
              {label && (
                <span className={classNames('font-semibold')}>{label}</span>
              )}
              {renderBadge?.()}
            </div>
            {description && (
              <span className={classNames('text-shade-40')}>{description}</span>
            )}
          </div>
        )}
        {renderTrailingIcon()}
        {renderArrow()}
      </div>

      {expandable
        ? isOpen &&
          items?.map((child) => (
            <SubmenuItem
              key={child._key}
              item={child}
              leadingIcon={child.iconClassName}
              disabled={disabled}
              size={size}
            />
          ))
        : null}
    </div>
  );
};
